import React, { useState } from 'react';
import { TonConnectButton, useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Wallet, Download, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import tonService from '../services/tonService';

const TONWalletConnect = ({ userId = 'demo_user' }) => {
  const userAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const [wallet, setWallet] = useState(null);
  const [mnemonic, setMnemonic] = useState('');
  const [importMnemonic, setImportMnemonic] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerateWallet = async () => {
    setLoading(true);
    try {
      const newWallet = await tonService.generateWallet(userId);
      setWallet(newWallet);
      setMnemonic(newWallet.mnemonic);
      toast.success('Wallet generated successfully!', {
        description: 'Save your mnemonic phrase securely.',
      });
    } catch (error) {
      toast.error('Failed to generate wallet', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportWallet = async () => {
    if (!importMnemonic || importMnemonic.trim().split(' ').length !== 24) {
      toast.error('Invalid mnemonic', {
        description: 'Please enter 24 words',
      });
      return;
    }

    setLoading(true);
    try {
      const importedWallet = await tonService.importWallet(userId, importMnemonic);
      setWallet(importedWallet);
      toast.success('Wallet imported successfully!');
      setImportMnemonic('');
    } catch (error) {
      toast.error('Failed to import wallet', {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      {/* TON Connect Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-6 w-6" />
            Connect TON Wallet
          </CardTitle>
          <CardDescription>
            Connect your Tonkeeper or other TON wallet
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {userAddress ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-600">✅ Connected</p>
                  <p className="text-xs text-gray-600 font-mono break-all">
                    {userAddress}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Connect your wallet to interact with TON blockchain
                </p>
              )}
            </div>
            <TonConnectButton />
          </div>
        </CardContent>
      </Card>

      {/* Wallet Management */}
      <Card>
        <CardHeader>
          <CardTitle>Wallet Management</CardTitle>
          <CardDescription>
            Generate new wallet or import existing one
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="generate" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">Generate New</TabsTrigger>
              <TabsTrigger value="import">Import Existing</TabsTrigger>
            </TabsList>

            <TabsContent value="generate" className="space-y-4">
              <div className="space-y-4">
                <Button
                  onClick={handleGenerateWallet}
                  disabled={loading}
                  className="w-full"
                  data-testid="generate-wallet-btn"
                >
                  {loading ? 'Generating...' : 'Generate New Wallet'}
                </Button>

                {wallet && mnemonic && (
                  <div className="space-y-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="text-yellow-600 mt-1">⚠️</div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-yellow-800">
                          Save your mnemonic phrase!
                        </p>
                        <p className="text-xs text-yellow-700">
                          Write it down and store it in a safe place. Never share it with anyone.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Mnemonic Phrase (24 words)</Label>
                      <div className="relative">
                        <div className="p-3 bg-white border rounded-md font-mono text-xs break-all">
                          {mnemonic}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute top-2 right-2"
                          onClick={() => copyToClipboard(mnemonic)}
                          data-testid="copy-mnemonic-btn"
                        >
                          {copied ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Wallet Address</Label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-white border rounded text-xs break-all">
                          {wallet.address}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(wallet.address)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="import" className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="import-mnemonic">Mnemonic Phrase (24 words)</Label>
                  <Input
                    id="import-mnemonic"
                    placeholder="word1 word2 word3 ..."
                    value={importMnemonic}
                    onChange={(e) => setImportMnemonic(e.target.value)}
                    className="font-mono text-sm"
                    data-testid="import-mnemonic-input"
                  />
                  <p className="text-xs text-gray-500">
                    Enter your 24-word mnemonic phrase separated by spaces
                  </p>
                </div>

                <Button
                  onClick={handleImportWallet}
                  disabled={loading || !importMnemonic}
                  className="w-full"
                  data-testid="import-wallet-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {loading ? 'Importing...' : 'Import Wallet'}
                </Button>

                {wallet && !mnemonic && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800">✅ Wallet imported!</p>
                    <code className="text-xs text-green-700 break-all">{wallet.address}</code>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default TONWalletConnect;
